import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import { getImageUrl } from '../lib/utils';

const Departments = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/departments/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([deptData, settingsData]) => {
        setDepartments(deptData);
        setSettings(settingsData);
      })
      .catch(error => console.error('Error fetching data:', error))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  const administrations = departments.filter(d => d.category === 'إدارة');
  const centers = departments.filter(d => d.category === 'مركز');

  const displayDepartments = activeTab === 'all' ? departments : 
                             activeTab === 'إدارة' ? administrations : centers;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <PageHero 
        title="إدارات ومراكز الجامعة"
        subtitle="تضم الجامعة مجموعة من الإدارات والمراكز المتخصصة لخدمة العملية التعليمية"
        backgroundImage={getImageUrl(settings?.page_departments_bg)}
      />

      {/* Departments and Centers */}
      <section id="departments-grid" className="py-20 bg-gray-50 dark:bg-[#071326] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <Tabs 
            defaultValue="all" 
            className="w-full mb-12" 
            onValueChange={(val) => {
              setActiveTab(val);
              const el = document.getElementById('departments-grid');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto bg-gray-200 dark:bg-[#0E2243]">
              <TabsTrigger value="all">الكل</TabsTrigger>
              <TabsTrigger value="إدارة">الإدارات</TabsTrigger>
              <TabsTrigger value="مركز">المراكز</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayDepartments.map((dept) => {
              const IconComponent = Icons[dept.icon];
              return (
                <Card key={dept.id} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white dark:bg-[#0E2243] border border-gray-100 dark:border-gray-800">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-[#0F388E] dark:bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A040] dark:group-hover:bg-[#C9A040] transition-colors">
                        {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                      </div>
                      <div className="flex-1">
                        <span className="inline-block px-3 py-1 bg-[#C9A040] text-white text-xs rounded-full mb-2">
                          {dept.category}
                        </span>
                        <h3 className="text-xl font-bold text-[#0F388E] dark:text-white group-hover:text-[#C9A040] dark:group-hover:text-[#FACC15] transition-colors">
                          {dept.name}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4 line-clamp-3">
                      {dept.description}
                    </p>
                    {dept.director_name && (
                      <div className="flex items-center gap-3 mb-4 bg-blue-50/70 dark:bg-gray-800/60 p-2.5 rounded-lg border border-blue-100/50 dark:border-gray-700">
                        {dept.director_image && (
                          <img
                            src={getImageUrl(dept.director_image)}
                            alt={dept.director_name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-[#C9A040] shadow-sm flex-shrink-0"
                          />
                        )}
                        <div className="text-xs">
                          <div className="text-gray-500 dark:text-gray-400 font-medium">{dept.director_title || 'مدير الإدارة'}:</div>
                          <div className="font-bold text-[#0F388E] dark:text-gray-200">{dept.director_name}</div>
                        </div>
                      </div>
                    )}
                    <Link to={`/departments/${dept.slug}`}>
                      <Button className="w-full bg-[#0F388E] hover:bg-[#0F388E]/90 dark:bg-[#123985] dark:hover:bg-[#1E4EAA] text-white">
                        عرض التفاصيل
                        <ArrowLeft className="mr-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white dark:bg-[#0B1A30] border-t border-gray-100 dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '10', label: 'إدارات ومراكز' },
              { number: '6', label: 'إدارات متخصصة' },
              { number: '4', label: 'مراكز علمية' },
              { number: '100+', label: 'موظف إداري' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#0F388E] dark:text-[#C9A040] mb-2">{stat.number}</div>
                <div className="text-gray-600 dark:text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Departments;